import { AIBasedAPISelectionParameterExtractionApiExtractParametersRequest } from '@ariksa/ai-engine';
import { AIAgentsApiGetResourceSummaryRequest } from '@ariksa/ai-engine/api';
import { WorkflowApiGetLatestRequest, WorkflowRead } from '@ariksa/awm/api';
import {
  BlueprintApiGetBlueprintsRequest,
  BlueprintExceptionRead,
  PageBluePrintsRead,
  PolicyExceptionApiAddExceptionRequest,
} from '@ariksa/compliance-policies/api';
import { FileDataApiGetScannersRequest } from '@ariksa/data-scanning/api';
import {
  TagsApiGetTagKeysRequest,
  TagsApiGetTagValueRequest,
} from '@ariksa/inventory-core';
import {
  ContextApiGetRiskContextResRequest,
  InsightV2ApiInsightV2AllRequest,
  InsightV2Pagination,
  LabelResponse,
  RegionLocation,
  ResourceApiGetRegionsRequest,
  ResourceApiGetRegionLocationsRequest,
  ResourceAnalyticsApiGetResourceAnalyticsRegionsRequest,
  RiskContextResponse,
  SearchApiIsMapAvailableRequest,
  TagsApiGetLabelsRequest,
} from '@ariksa/inventory-core/api';
import {
  AlertLogsApiGetLogsForIdsRequest,
  AlertLogsResponse,
  AlertResourceResponse,
  AlertsApiGetAlertSummaryRequest,
  AlertsApiGetResourceDetailsRequest,
  AlertSummaryResponse,
  AlertWorkflowResponse,
  ClientsApiGetClientDetailsRequest,
  ClientsApiNotifyClientRequest,
} from '@ariksa/notification/api';
import {
  FaqApiGetAllQuestionsWithAnswersRequest,
  FaqRead,
  FeatureRead,
} from '@ariksa/profile/api';
import { ReportsApiSendToEsRequest } from '@ariksa/reporting';
import flagsmith from '@flagsmith/flagsmith';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, takeLatest } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';
import { ItemApiTriggerInstanceScanRequest } from 'services/Vulnerability/legacyRequestTypes';

import {
  AlertVaultAlertService,
  AlertVaultClientService,
  AlertVaultDeliveryService,
  AlertVaultWorkflowService,
  isAlertVaultEnabled,
} from 'api/alert-vault/client';
import { alertTicketLogs } from 'api/alert-vault/jira';
import {
  getScanVaultCentralScanningAccounts,
  ScanVaultCentralAccount,
  getScanVaultScannerInfo,
  ScanVaultScannerInfo,
  triggerScanVaultScan,
} from 'api/scan-vault/client';
import {
  AIEngineService,
  ComplianceService,
  DataScanningService,
  InventoryService,
  NotificationService,
  ProfileService,
  ReportingService,
  serviceClient,
  WorkflowService,
} from 'app/api';
import { LegacyScanAnalysis } from 'app/api/services/legacyScanAnalysis';
import { doGetResourceInsight } from 'containers/ActiveCloudResource/saga';
import { doGetAlertCount } from 'containers/Dashboard/saga';
import { TagValuesResponse } from 'containers/SharedState/types';

import { actions } from './slice';

export function* sharedStateSaga() {
  yield takeLatestAction(actions.getRegions, doGetRegions);
  yield takeLatestAction(actions.getRegionsByProvider, doGetRegionLocations);
  yield takeLatestAction(actions.getTagKeys, doGetTagKeys);
  yield takeLatestAction(actions.getTagValues, doGetTagValues);
  yield takeLatestAction(actions.getLastDiscoveryTime, doGetLastDiscoveryTime);
  yield takeLatestAction(actions.notifyClient, doNotifyClient);
  yield takeLatestAction(actions.getFAQ, doGetFAQ);
  yield takeLatestAction(actions.getLabels, doGetLabels);
  yield takeLatestAction(actions.getInsightV2All, doGetInsightV2All);
  yield takeLatestAction(actions.getResourceInsight, doGetResourceInsight);
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(actions.getBlueprints, doGetBlueprints);
  yield takeLatestAction(
    actions.exportAlertsToElasticSearch,
    doExportAlertsToElasticSearch,
  );
  yield takeLatestAction(
    actions.getAlertResourceTypes,
    doGetAlertResourceTypes,
  );
  yield takeLatest(actions.getClients.type, doGetClients);
  yield takeLatestAction(actions.getWorkflows, doGetWorkflows);
  yield takeLatestAction(actions.addException, doAddException);
  yield takeLatestAction(
    actions.extractParametersForSearch,
    doExtractParametersForSearch,
  );
  yield takeLatestAction(
    actions.getSnapshotStatus,
    doGetCloudAccountSnapshotStatus,
  );
  yield takeLatestAction(actions.getAlertListStatus, doGetAlertListStatus);
  yield takeLatestAction(actions.getMyTasks, doGetMyTasks);
  yield takeLatestAction(actions.getAIResourceSummary, doGetAIResourceSummary);
  yield takeLatestAction(actions.getDataScannerInfo, doGetDataScannerInfo);
  yield takeLatestAction(
    actions.getVulnerabilityScannerInfo,
    doGetVulnerabilityScannerInfo,
  );
  yield takeLatestAction(
    actions.getSecurityGraphAvailability,
    doGetSecurityGraphAvailability,
  );
  yield takeLatestAction(actions.getAlertCategories, doGetAlertCategories);
  yield takeLatestAction(actions.getAlertCount, doGetAlertCount);

  yield takeLatestAction(actions.triggerInstanceScan, doTriggerInstanceScan);
  yield takeLatestAction(
    actions.getCentralVulnerabilityScanners,
    doGetCentralVulnerabilityScanners,
  );
}

/*get regions*/
function* doGetRegions(
  ctx: QueryContext<
    string[],
    | ResourceApiGetRegionsRequest
    | ResourceAnalyticsApiGetResourceAnalyticsRegionsRequest
  >,
) {
  const request = ctx.params as any;
  yield call(
    ctx.fetch,
    () =>
      request.useInventoryV3
        ? InventoryService.ResourceAnalytics.getResourceAnalyticsRegions(
            request,
          )
        : InventoryService.Resource.getRegions(request),
    {
      map: r => r?.data?.regions ?? r?.regions ?? r?.data,
      errorMsg: 'Failed to get regions.',
    },
  );
}

/*get region locations*/
function* doGetRegionLocations(
  ctx: QueryContext<RegionLocation[], ResourceApiGetRegionLocationsRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Resource.getRegionLocations(ctx.params),
    {
      errorMsg: 'Failed to get region locations.',
    },
  );
}

/*get tag keys*/
function* doGetTagKeys(ctx: QueryContext<string[], TagsApiGetTagKeysRequest>) {
  yield call(ctx.fetch, () => InventoryService.Tags.getTagKeys(ctx.params), {
    errorMsg: 'Failed to get tag keys.',
  });
}

/*get tag values*/
function* doGetTagValues(
  ctx: QueryContext<TagValuesResponse, TagsApiGetTagValueRequest>,
) {
  yield call(ctx.fetch, () => InventoryService.Tags.getTagValue(ctx.params), {
    map: r => ({ key: ctx.params.keys, values: r?.data }),
    errorMsg: 'Failed to get tag values.',
  });
}

export function* doGetLastDiscoveryTime(
  ctx: QueryContext<WorkflowRead[], WorkflowApiGetLatestRequest>,
) {
  yield call(ctx.fetch, () => WorkflowService.getLatest(ctx.params), {
    errorMsg: 'Failed to get last refreshed time.',
  });
}

function* doNotifyClient(
  ctx: QueryContext<any, ClientsApiNotifyClientRequest>,
) {
  yield call(
    ctx.fetch,
    () => {
      const notify = ctx.params?.notifyClients;
      return isAlertVaultEnabled() && notify?.client_id && notify?.alert_uuid
        ? AlertVaultDeliveryService.create({
            alert_id: notify.alert_uuid,
            client_id: notify.client_id,
            subject: notify.subject,
            body: notify.message ?? notify.ticket_fields?.summary,
            metadata: notify.ticket_fields,
          })
        : NotificationService.Clients.notifyClient(ctx.params);
    },
    { errorMsg: 'Failed to notify client.' },
  );
}

function* doGetFAQ(
  ctx: QueryContext<FaqRead[], FaqApiGetAllQuestionsWithAnswersRequest>,
) {
  yield call(
    ctx.fetch,
    () => ProfileService.FAQ.getAllQuestionsWithAnswers(ctx.params),
    { errorMsg: 'Failed to get question and answers.' },
  );
}

export function* doGetLabels(
  ctx: QueryContext<LabelResponse[], TagsApiGetLabelsRequest>,
) {
  yield call(ctx.fetch, () => InventoryService.Tags.getLabels(ctx.params), {
    errorMsg: 'Failed to get labels!',
  });
}

export function* doGetInsightV2All(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2AllRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.insightV2All(ctx.params),
    {
      errorMsg: 'Failed to get data!',
    },
  );
}

export function* doGetRiskContext(
  ctx: QueryContext<
    Record<string, RiskContextResponse[]>,
    ContextApiGetRiskContextResRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Context.getRiskContextRes(ctx.params),
    {
      errorMsg: 'Failed to get risk context!',
    },
  );
}

export function* doGetAlertResourceTypes(
  ctx: QueryContext<AlertResourceResponse, AlertsApiGetResourceDetailsRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.resourceTypes({
            accountId: ctx.params.accountId ?? undefined,
            environmentId: ctx.params.environmentId ?? undefined,
          })
        : NotificationService.Alerts.getResourceDetails(ctx.params),
    {
      errorMsg: 'Failed to get resource types!',
    },
  );
}

//get blueprints
export function* doGetBlueprints(
  ctx: QueryContext<PageBluePrintsRead, BlueprintApiGetBlueprintsRequest>,
) {
  yield call(
    ctx.fetch,
    async () => ComplianceService.Blueprint.getBlueprints(ctx.params),
    { errorMsg: 'Failed to get blueprints!' },
  );
}

//export Alerts
export function* doExportAlertsToElasticSearch(
  ctx: QueryContext<any, ReportsApiSendToEsRequest>,
) {
  yield call(
    ctx.fetch,
    async () => ReportingService.Reports.sendToEs(ctx.params),
    {
      errorMsg: 'Failed to export alerts to Elastic Search.',
      successMsg: 'Successfully sent alerts to Elastic Search.',
    },
  );
}

export function* doGetClients(
  action: PayloadAction<ClientsApiGetClientDetailsRequest>,
) {
  yield call(serviceClient, {
    fn: async () =>
      isAlertVaultEnabled()
        ? AlertVaultClientService.list(action.payload.clientName)
        : NotificationService.Clients.getClientDetails(action.payload),
    onSuccess: actions.clientsLoaded,
    onError: actions.clientsLoadingError,
    mapResponse: res => ({
      clientName: action.payload.clientName,
      data: res?.data,
    }),
    errorTitle: 'Failed to get clients!',
  });
}

/*get alert workflows*/
function* doGetWorkflows(ctx: QueryContext<AlertWorkflowResponse[]>) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultWorkflowService.list()
        : NotificationService.AlertWorkflow.getWorkflow({}),
    { errorMsg: 'Failed to get workflows!' },
  );
}

/*add exception*/
function* doAddException(
  ctx: QueryContext<
    BlueprintExceptionRead,
    PolicyExceptionApiAddExceptionRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => ComplianceService.PolicyException.addException(ctx.params),
    { errorMsg: 'Failed to add exception!' },
  );
}

/*extract parameters for search*/
function* doExtractParametersForSearch(
  ctx: QueryContext<
    any,
    AIBasedAPISelectionParameterExtractionApiExtractParametersRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => AIEngineService.AIParamExtraction.extractParameters(ctx.params),
    { errorMsg: 'Failed to extract parameters for search!' },
  );
}

export function* doGetCloudAccountSnapshotStatus(
  ctx: QueryContext<WorkflowRead[], WorkflowApiGetLatestRequest>,
) {
  yield call(ctx.fetch, () => WorkflowService.getLatest(ctx.params), {
    errorMsg: 'Failed to get last discovery time!',
  });
}

//get alert workflow status (for ex. for Slack, jira tickets etc.)
function* doGetAlertListStatus(
  ctx: QueryContext<AlertLogsResponse[], AlertLogsApiGetLogsForIdsRequest>,
) {
  yield call(
    ctx.fetch,
    async () => {
      const alertIds = ctx.params.alertId ?? [];
      if (!isAlertVaultEnabled() || alertIds.length === 0) {
        return NotificationService.AlertsLogs.getLogsForIds(ctx.params);
      }
      // One tickets request per page instead of an activity request per
      // alert; the column shows ticket status, which activity never held.
      return alertTicketLogs(alertIds);
    },
    { errorMsg: 'Failed to get alert status!' },
  );
}

//get alert workflow status (for ex. for Slack, jira tickets etc.)
function* doGetMyTasks(ctx: QueryContext<FeatureRead[]>) {
  yield call(ctx.fetch, () => ProfileService.Journey.getAllFeatures(), {
    errorMsg: 'Failed to get my tasks!',
    cacheKey: 'myTasks',
  });
}

function* doGetAIResourceSummary(
  ctx: QueryContext<any, AIAgentsApiGetResourceSummaryRequest>,
) {
  yield call(
    ctx.fetch,
    () => AIEngineService.AIAgent.getResourceSummary(ctx.params),
    {
      errorMsg: 'Failed to get resource summary!',
    },
  );
}

export function* doGetDataScannerInfo(
  ctx: QueryContext<Record<string, any>, FileDataApiGetScannersRequest>,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.FileData.getScanners(ctx.params),
    {
      errorMsg: 'Failed to get data scanner info!',
      cacheKey: 'dataScannerInfo',
    },
  );
}

export function* doGetVulnerabilityScannerInfo(
  ctx: QueryContext<Record<string, ScanVaultScannerInfo[]>>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(ctx.fetch as any, getScanVaultScannerInfo, {
      map: value => value,
      errorMsg: 'Failed to get data scanner info!',
      cacheKey: 'vulnerabilityScannerInfo',
    });
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getVulnerabilityScanners(),
    {
      errorMsg: 'Failed to get data scanner info!',
      cacheKey: 'vulnerabilityScannerInfo',
    },
  );
}

export function* doGetSecurityGraphAvailability(
  ctx: QueryContext<Record<string, boolean>, SearchApiIsMapAvailableRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Search.isMapAvailable(ctx.params),
    {
      errorMsg: 'Failed to get security graph availability!',
    },
  );
}

export function* doGetAlertCategories(
  ctx: QueryContext<AlertSummaryResponse, AlertsApiGetAlertSummaryRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.categories({
            accountId: ctx.params.accountId ?? undefined,
            environmentId: ctx.params.environmentId ?? undefined,
          })
        : NotificationService.Alerts.getAlertSummary(ctx.params),
    {
      errorMsg: 'Failed to get alert categories!',
    },
  );
}

export function* doTriggerInstanceScan(
  ctx: QueryContext<any, ItemApiTriggerInstanceScanRequest>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    const params = (ctx.params as any) ?? {};
    yield call(
      ctx.fetch as any,
      () =>
        triggerScanVaultScan({
          resourceUUID: params.resourceUUID ?? params.resource_uuid,
          nativeId: params.instanceId ?? params.instance_id,
          accountId: params.accountId ?? params.account_id,
          region: params.region,
          resourceType: params.resourceType ?? params.resource_type,
        }),
      {
        map: value => value,
        errorMsg: 'Failed to trigger scan for resource!',
        successMsg: 'Successfully triggered scan for resource!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.triggerInstanceScan(ctx.params),
    {
      errorMsg: 'Failed to trigger scan for resource!',
      successMsg: 'Successfully triggered scan for resource!',
    },
  );
}

export function* doGetCentralVulnerabilityScanners(
  ctx: QueryContext<ScanVaultCentralAccount[]>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(ctx.fetch as any, getScanVaultCentralScanningAccounts, {
      map: value => value,
      errorMsg: 'Failed to central scanning accounts!',
    });
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getCentralScanningAccounts(ctx.params),
    {
      errorMsg: 'Failed to central scanning accounts!',
    },
  );
}
